import {
  MobileRow,
  MobileRowTitle,
  MobileRowValue,
  MobileTableRowWrap,
  MobileTableWrapper,
  NoResults
} from '@/components/table/mobileTable/mobileTable';

import { Allocation_Status, CompliancePageListAllocationComplianceQuery } from '@/__generated__/graphql';
import ActionDropdown from '@/components/basicComponents/actionDropdown';
import Tag from '@/components/basicComponents/tag';
import { TableColumns } from '@/components/table/types';
import { useAuth } from '@/hooks/use-auth';
import { getUTCDate } from '@/shared/utils/date';
import { TenantType } from '@trace-8/trace8-schema';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getAllocationHistoryTableDetailsCell } from './table';

interface AllocationHistoryMobileTableProps {
  tableColumns: TableColumns[];
  tableRows: CompliancePageListAllocationComplianceQuery['ListAllocationCompliance']['allocations'];
  openPage: (path: string) => void;
  viewSignaturePacket: (id: string) => Promise<void>;
}

const AllocationHistoryMobileTable = ({ tableColumns, tableRows, openPage, viewSignaturePacket }: AllocationHistoryMobileTableProps) => {
  const { user } = useAuth();
  const { t } = useTranslation(['enums']);

  const TableRow = ({ el }: { el: CompliancePageListAllocationComplianceQuery['ListAllocationCompliance']['allocations'][number] }) => {
    return (
      <MobileTableRowWrap key={el.id}>
        <MobileRow>
          <MobileRowTitle>{tableColumns[0].title}</MobileRowTitle>
          <MobileRowValue
            onClick={() => openPage(`/investors/${el.legalEntity?.family?.id}/${el.legalEntity?.id}?backToTitle=Allocation History`)}
            isRedirect
          >
            {el.legalEntity.entityName}
          </MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{tableColumns[1].title}</MobileRowTitle>
          <MobileRowValue onClick={() => openPage(`/strategyDetails/${el?.investment?.id}?backToTitle=Allocation History`)} isRedirect>
            {el?.investment?.name}
          </MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{tableColumns[2].title}</MobileRowTitle>
          <MobileRowValue>{el.statusDate && getUTCDate(el.statusDate)}</MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{tableColumns[3].title}</MobileRowTitle>
          <MobileRowValue>
            {el?.status && (
              <Tag styleType={el?.status === Allocation_Status.Committed ? 'valid' : 'warning'} size="md">
                {t(`enums:ALLOCATION_STATUS.${el?.status}`) ?? ''}
              </Tag>
            )}
          </MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{tableColumns[4].title}</MobileRowTitle>
          <MobileRowValue>{getAllocationHistoryTableDetailsCell(el?.details, el?.status)}</MobileRowValue>
        </MobileRow>

        {user && user.tenant.type === TenantType.PLATFORM_MANAGER && (
          <MobileRow>
            <MobileRowTitle>{tableColumns[5].title}</MobileRowTitle>
            <MobileRowValue>{el.advisoryFirm.name}</MobileRowValue>
          </MobileRow>
        )}

        {!!el?.subscriptionDocuments?.length && (
          <MobileRow>
            <ActionDropdownWrapper>
              <ActionDropdown
                actionDropdownListPosition="center"
                options={[
                  {
                    name: 'View Signature Packet',
                    onClick: () => viewSignaturePacket(el.subscriptionDocuments?.[0]?.id)
                  }
                ]}
              />
            </ActionDropdownWrapper>
          </MobileRow>
        )}
      </MobileTableRowWrap>
    );
  };

  return (
    <MobileTableWrapper>
      {tableRows.length ? (
        tableRows.map((el) => {
          // eslint-disable-next-line react/prop-types
          return <TableRow el={el} key={el.id} />;
        })
      ) : (
        <NoResults>No Results</NoResults>
      )}
    </MobileTableWrapper>
  );
};

export default AllocationHistoryMobileTable;

const ActionDropdownWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;
